// PartnerProgrammes · two side-by-side programme cards (Introducer +
// Ambassador) plus a third "Custom Schemes" card · mirrors the actual
// structure published at ofinancial.markets/partner/.
import { Handshake, BadgeCheck, Briefcase, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function buildProgrammes(t) {
  return [
    {
      id: "introducer",
      Icon: Handshake,
      pill: t("partner.programmes.introducer.pill", "Volume-based rebates"),
      title: t("partner.programmes.introducer.title", "Introducer"),
      headline: t("partner.programmes.introducer.headline", "Up to 50%"),
      headlineSub: t("partner.programmes.introducer.headlineSub", "spread revenue share"),
      body: t("partner.programmes.introducer.body", "Refer clients and earn rebates on every lot they trade. The more active traders you onboard, the higher your daily payout."),
      points: [
        t("partner.programmes.introducer.p1", "Up to 50% of spread revenue per active trader"),
        t("partner.programmes.introducer.p2", "Compounds with your referral network size"),
        t("partner.programmes.introducer.p3", "Daily payouts · transparent reporting"),
      ],
      cta: { label: t("partner.programmes.introducer.cta", "Become Introducer"), to: "/partner#apply" },
      featured: true,
    },
    {
      id: "ambassador",
      Icon: BadgeCheck,
      pill: t("partner.programmes.ambassador.pill", "Hybrid · fixed + variable"),
      title: t("partner.programmes.ambassador.title", "Ambassador"),
      headline: t("partner.programmes.ambassador.headline", "Fixed fee"),
      headlineSub: t("partner.programmes.ambassador.headlineSub", "+ spread revenue"),
      body: t("partner.programmes.ambassador.body", "A guaranteed monthly fee on top of spread revenue share. Designed for content creators, educators and regional ambassadors building long-term audiences."),
      points: [
        t("partner.programmes.ambassador.p1", "Guaranteed monthly base fee"),
        t("partner.programmes.ambassador.p2", "Plus spread-revenue rebates on referrals"),
        t("partner.programmes.ambassador.p3", "Co-marketing & dedicated account manager"),
      ],
      cta: { label: t("partner.programmes.ambassador.cta", "Become Ambassador"), to: "/partner#apply" },
    },
    {
      id: "custom",
      Icon: Briefcase,
      pill: t("partner.programmes.custom.pill", "Talk to us"),
      title: t("partner.programmes.custom.title", "Custom Schemes"),
      headline: t("partner.programmes.custom.headline", "Bespoke"),
      headlineSub: t("partner.programmes.custom.headlineSub", "structured deals"),
      body: t("partner.programmes.custom.body", "Large networks, regional master-IBs and white-label arrangements are handled directly with our Partner Account Managers. Terms tailored to your reach."),
      points: [
        t("partner.programmes.custom.p1", "Master-IB / multi-tier structures"),
        t("partner.programmes.custom.p2", "Regional exclusivity options"),
        t("partner.programmes.custom.p3", "White-label & API integrations"),
      ],
      cta: { label: t("partner.programmes.custom.cta", "Contact partnership team"), to: "/contact" },
    },
  ];
}

function ProgrammeCard({ p, idx }) {
  const { t } = useTranslation();
  // Force-scroll to the target hash even when the URL already has it
  // (react-router-dom v6 skips re-rendering when pathname+hash are
  // unchanged, so the ScrollToTop effect never fires on a 2nd click).
  // For external paths (/contact, /partner-x) we let <Link> behave
  // normally so the page actually navigates.
  const onCtaClick = (e) => {
    const to = p.cta.to;
    if (!to.includes("#")) return; // normal navigation · let Link handle it
    const [path, hash] = to.split("#");
    if (window.location.pathname === path && hash) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <motion.article
      id={p.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.55, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`card-beam scroll-mt-24 ${p.featured ? "ring-1 ring-brand/30" : ""} relative flex flex-col`}
      data-testid={`partner-programme-${p.id}`}
    >
      {/* Featured ribbon */}
      {p.featured && (
        <div className="absolute top-3 right-3 pill-mono z-10" style={{ color: "var(--brand)" }}>
          {t("partner.programmes.mostPopular", "Most popular")}
        </div>
      )}

      <div className="p-6 lg:p-7 flex flex-col h-full">
        {/* Icon · sits on a soft radial halo (no boxed tile) */}
        <div className="relative w-12 h-12 mb-4 grid place-items-center">
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent) 0%, transparent 70%)",
              filter: "blur(2px)",
            }}
          />
          <p.Icon size={26} strokeWidth={1.6} style={{ color: "var(--brand)", position: "relative", zIndex: 1 }} />
        </div>

        <div className="pill-mono inline-flex mb-3 self-start">{p.pill}</div>
        <h3 className="font-display text-2xl font-medium tracking-[-0.015em] text-text-1 mb-3">{p.title}</h3>

        {/* Headline metric */}
        <div className="mb-4">
          <div className="font-display text-[40px] leading-none font-semibold tracking-[-0.025em] text-gradient-brand">
            {p.headline}
          </div>
          <div className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3 mt-1.5">{p.headlineSub}</div>
        </div>

        <p className="text-text-3 text-[13.5px] leading-relaxed font-light mb-5">{p.body}</p>

        <ul className="space-y-2 mb-7">
          {p.points.map((pt) => (
            <li key={pt} className="flex items-start gap-2 text-[13px] text-text-2">
              <Check size={14} strokeWidth={2.2} style={{ color: "var(--brand)" }} className="mt-0.5 shrink-0" />
              <span>{pt}</span>
            </li>
          ))}
        </ul>

        <Link
          to={p.cta.to}
          onClick={onCtaClick}
          data-testid={`partner-cta-${p.id}`}
          className={`mt-auto ${p.featured ? "btn-solid-brand" : "btn-ghost-rotating"} !h-11 !text-[12px] !uppercase !tracking-[0.18em]`}
        >
          {p.cta.label} <ArrowRight size={13} />
        </Link>
      </div>
    </motion.article>
  );
}

export default function PartnerProgrammes() {
  const { t } = useTranslation();
  const programmes = buildProgrammes(t);
  return (
    <section className="relative py-12 lg:py-16" data-testid="partner-programmes">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <div className="pill-mono inline-flex mb-3">{t("partner.programmes.pill", "Two main programmes · plus custom")}</div>
          <h2 className="font-display text-2xl/[1.25] lg:text-3xl/[1.25] font-medium tracking-[-0.02em] text-text-1 mb-3">
            {t("partner.programmes.title", "Pick the partner track")}{" "}
            <span className="text-gradient-brand">{t("partner.programmes.titleAccent", "that fits your network")}</span>
          </h2>
          <p className="text-text-3 text-[14.5px] lg:text-[15px] font-light leading-relaxed max-w-2xl mx-auto">
            {t("partner.programmes.subtitle", "Whether you refer one client a month or run a global IB network, there's a programme calibrated to your reach. Mix and match with custom terms for larger structures.")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
          {programmes.map((p, i) => (
            <ProgrammeCard key={p.id} p={p} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
